import React, {useEffect, useRef, useState} from "react";
import {Building2, ShieldCheck} from "lucide-react";
import {baseCommitments, company, CONTACT_EMAIL, contractTerm, Design, optionPaths, phases, sectionCode, services} from "./FirstSFGData";

const {baseYears, optionYears, meetingsPerWeek, hoursPerWeek} = contractTerm
const totalYears = baseYears + optionYears
const termSummary = `${baseYears}-year base period with up to ${optionYears} option years (${totalYears} years total)`

interface props {
    design: Design
    onBriefBarChange: (isVisible: boolean) => void
}

const FirstSFGShowcase = ({design, onBriefBarChange}: props) => {
    const [selected, setSelected] = useState<string[]>(['ceremony', 'board'])
    const [isGridInView, setIsGridInView] = useState(false)
    const gridRef = useRef<HTMLUListElement>(null)
    const briefRef = useRef<HTMLElement>(null)

    // On small screens the brief sits below all the cards, so a docked bar keeps the selection visible.
    useEffect(() => {
        const grid = gridRef.current
        if (!grid) return
        const observer = new IntersectionObserver(([entry]) => {
            // The bar only exists below the desktop breakpoint, where the brief is not sticky.
            const isVisible = entry.isIntersecting && !window.matchMedia('(min-width: 1100px)').matches
            setIsGridInView(isVisible)
            onBriefBarChange(isVisible)
        }, {rootMargin: '0px 0px -30% 0px'})
        observer.observe(grid)
        return () => observer.disconnect()
    }, [onBriefBarChange])

    const toggle = (id: string) =>
        setSelected(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id])

    const chosen = services.filter(service => selected.includes(service.id))

    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('1st SFG web support')}&body=${encodeURIComponent(
        `Hi HITL Engineering,\n\nWe're interested in the following for 1st Special Forces Group:\n\n${chosen.map(service => `- ${service.title}`).join('\n')}\n\nTerm: ${termSummary}\n\nBest time to talk:\n`
    )}`

    return (
        <div className={'sfg-showcase'}>
            <div className={'sfg-section-head'}>
                <p className={'sfg-section-code'}>{sectionCode(design, '04', 'Showcase · HITL Engineering')}</p>
                <h2>How we can support First Group</h2>
                <p className={'sfg-section-copy'}>
                    Everything above is a working example of what we build. Here is how we would put it to work for the Group.
                    Select the capabilities you want and we will draft the brief.
                </p>
            </div>

            <div className={'sfg-credentials'}>
                <p className={'sfg-credentials-name'}>{company.legalName}</p>
                <ul aria-label={'Business credentials'}>
                    <li><Building2 size={16} aria-hidden={true}/>{company.registration}</li>
                    {company.insurance.map(policy => (
                        <li key={policy}><ShieldCheck size={16} aria-hidden={true}/>{policy} insurance</li>
                    ))}
                </ul>
            </div>

            <div className={'sfg-showcase-layout'}>
                <ul className={'sfg-service-grid'} ref={gridRef}>
                    {services.map(service => {
                        const isSelected = selected.includes(service.id)
                        return (
                            <li key={service.id} className={`sfg-service ${isSelected ? 'selected' : ''}`}>
                                <div className={'sfg-service-head'}>
                                    <span className={'sfg-service-code'}>{service.code}</span>
                                    <h3>{service.title}</h3>
                                </div>
                                <p className={'sfg-service-pitch'}>{service.pitch}</p>
                                <p className={'sfg-panel-label'}>For example</p>
                                <ul className={'sfg-service-examples'}>
                                    {service.examples.map(example => <li key={example}>{example}</li>)}
                                </ul>
                                <button className={'sfg-service-toggle'} aria-pressed={isSelected} onClick={() => toggle(service.id)}>
                                    {isSelected ? '✓ In package' : '+ Add to package'}
                                </button>
                            </li>
                        )
                    })}
                </ul>

                <aside className={'sfg-brief'} aria-label={'Your package'} ref={briefRef}>
                    <span className={'sfg-corner tl'} aria-hidden={true}/>
                    <span className={'sfg-corner br'} aria-hidden={true}/>
                    <p className={'sfg-panel-label'}>Mission brief</p>
                    <p className={'sfg-brief-line'}><span>To</span> HITL Engineering</p>
                    <p className={'sfg-brief-line'}><span>From</span> 1st SFG(A)</p>
                    <p className={'sfg-brief-line'}><span>Subj</span> Web support package</p>
                    <p className={'sfg-brief-line'}><span>Term</span> {baseYears} yr base + {optionYears} option</p>
                    <ol className={'sfg-brief-list'}>
                        {chosen.length > 0 ?
                            chosen.map(service => <li key={service.id}>{service.title}</li>)
                            :
                            <li className={'sfg-brief-empty'}>Nothing selected yet. Add a capability to start.</li>
                        }
                    </ol>
                    <p className={'sfg-brief-count'}>{chosen.length} of {services.length} capabilities</p>
                    {chosen.length > 0 ?
                        <a className={'sfg-btn sfg-btn-primary sfg-btn-block'} href={mailto}>Send brief</a>
                        :
                        <span className={'sfg-btn sfg-btn-primary sfg-btn-block disabled'} aria-disabled={true}>Send brief</span>
                    }
                    <p className={'sfg-fine'}>Opens an email to {CONTACT_EMAIL}.</p>
                </aside>
            </div>

            <div className={`sfg-brief-bar ${isGridInView ? 'show' : ''}`} aria-hidden={!isGridInView}>
                <span><strong>{chosen.length}</strong> of {services.length} in package</span>
                <button
                    className={'sfg-btn sfg-btn-primary'}
                    tabIndex={isGridInView ? 0 : -1}
                    onClick={() => briefRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'})}
                >
                    Review brief
                </button>
            </div>

            <div className={'sfg-term'}>
                <div className={'sfg-term-head'}>
                    <p className={'sfg-panel-label'}>Contract term</p>
                    <h3>{baseYears}-year base, up to {optionYears} option years</h3>
                    <p className={'sfg-term-copy'}>
                        For the first {baseYears} years, HITL Engineering runs the entire website for the Group, with a meeting every week.
                        After that, the Group can extend for up to {optionYears} more years, {totalYears} in all.
                        We would like to keep managing it the whole way, but how the option years run is the Group's call.
                    </p>
                </div>
                <div className={'sfg-term-track'} aria-hidden={true}>
                    <span className={'sfg-term-band base'} style={{gridColumn: `span ${baseYears}`}}>Base</span>
                    <span className={'sfg-term-band option'} style={{gridColumn: `span ${optionYears}`}}>Option years</span>
                    {Array.from({length: totalYears}, (_, i) => (
                        <span key={i} className={`sfg-term-year ${i < baseYears ? 'base' : 'option'}`}>Y{i + 1}</span>
                    ))}
                </div>
                <dl className={'sfg-term-legend'}>
                    <div>
                        <dt><span className={'sfg-term-swatch base'} aria-hidden={true}/>Base period</dt>
                        <dd>Years 1-{baseYears} · Fully managed, weekly meetings</dd>
                    </div>
                    <div>
                        <dt><span className={'sfg-term-swatch option'} aria-hidden={true}/>Option period</dt>
                        <dd>Years {baseYears + 1}-{totalYears} · We keep managing it, or you take it over</dd>
                    </div>
                </dl>
            </div>

            <div className={'sfg-commit'}>
                <div className={'sfg-commit-head'}>
                    <p className={'sfg-panel-label'}>What the base period includes</p>
                    <h3>You send it. We handle the rest.</h3>
                </div>
                <dl className={'sfg-commit-stats'}>
                    <div>
                        <dt>{meetingsPerWeek}</dt>
                        <dd>meeting every week</dd>
                    </div>
                    <div>
                        <dt>{hoursPerWeek} hrs</dt>
                        <dd>of work every week</dd>
                    </div>
                    <div>
                        <dt>100%</dt>
                        <dd>owned by the Group</dd>
                    </div>
                </dl>
                <ul className={'sfg-commit-grid'}>
                    {baseCommitments.map(group => (
                        <li key={group.id} className={'sfg-commit-card'}>
                            <h4>{group.title}</h4>
                            <p className={'sfg-commit-lead'}>{group.lead}</p>
                            <ul className={'sfg-commit-items'}>
                                {group.items.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </li>
                    ))}
                </ul>
            </div>

            <div className={'sfg-options'}>
                <div className={'sfg-options-head'}>
                    <p className={'sfg-panel-label'}>Years {baseYears + 1}-{totalYears}</p>
                    <h3>The option years are your call</h3>
                    <p className={'sfg-term-copy'}>Either way, the Group can change course at any option year.</p>
                </div>
                <ul className={'sfg-options-grid'}>
                    {optionPaths.map(path => (
                        <li key={path.id} className={`sfg-option ${path.id === 'managed' ? 'preferred' : ''}`}>
                            <span className={'sfg-option-tag'}>{path.tag}</span>
                            <h4>{path.title}</h4>
                            <p className={'sfg-commit-lead'}>{path.body}</p>
                            <ul className={'sfg-commit-items'}>
                                {path.items.map(item => <li key={item}>{item}</li>)}
                            </ul>
                        </li>
                    ))}
                </ul>
            </div>

            <div className={'sfg-phases'}>
                <p className={'sfg-panel-label'}>How it works</p>
                <ol className={'sfg-phase-list'}>
                    {phases.map(phase => (
                        <li key={phase.code} className={'sfg-phase'}>
                            <span className={'sfg-phase-code'}>{phase.code}</span>
                            <h3>{phase.title}</h3>
                            <p>{phase.body}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    )
}
export default FirstSFGShowcase
